import React, { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useT, type TranslationKey } from '../i18n'
import { ensureFarmList } from '../utils/farmWizard'
import { loadActiveContextSelection } from '../utils/activeContextSelection'
import { isAnyCrewModeStreamEnabled } from '../screens/crewMode/crewModeStreams'
import './Sidebar.css'

type NavItem = {
  labelKey: TranslationKey
  path: string
}

export const PRIMARY_NAV_ITEMS: NavItem[] = [
  { labelKey: 'nav.home', path: '/home' },
  { labelKey: 'nav.database', path: '/database' },
  { labelKey: 'nav.water', path: '/water' },
  { labelKey: 'nav.feed', path: '/feed' },
  { labelKey: 'nav.warehouse', path: '/inventory' },
  { labelKey: 'nav.sampling', path: '/biomass' },
  { labelKey: 'nav.lots', path: '/lots' },
  { labelKey: 'nav.tasks', path: '/tasks' },
  { labelKey: 'nav.reports', path: '/reports' },
  { labelKey: 'nav.costs', path: '/costs' },
  // Findings #142: Colaboración is outside the validation — hidden (the
  // routes stay). The mortality / SFR matrices live in Configuración only.
  { labelKey: 'nav.dashboard', path: '/dashboard-new' }
]


const hasWindow = typeof window !== 'undefined'

export default function Sidebar() {
  const t = useT()
  const location = useLocation()
  const pathname = location.pathname.toLowerCase()

  const [isMobile, setIsMobile] = useState(() =>
    hasWindow ? window.matchMedia('(max-width: 900px)').matches : false
  )

  // Crew Mode pilot shell v0: show a single `nav.crewMode` entry only when
  // at least one Crew Mode capture stream is flag-enabled for the active
  // farm, so Crew Mode stays invisible on non-pilot farms. The flag reads
  // are local-only; `ensureFarmList` is served from cache.
  const [showCrewModeNav, setShowCrewModeNav] = useState(false)

  useEffect(() => {
    let cancelled = false
    void ensureFarmList().then((farms) => {
      if (cancelled) return
      const selectedFarmId = loadActiveContextSelection().farmId
      const activeFarmId = selectedFarmId && farms.some((farm) => String(farm.id) === selectedFarmId)
        ? selectedFarmId
        : String(farms[0]?.id ?? '')
      setShowCrewModeNav(isAnyCrewModeStreamEnabled(activeFarmId))
    })
    return () => {
      cancelled = true
    }
  }, [pathname])
  const [isOpen, setIsOpen] = useState(() => {
    if (!hasWindow) return true
    const query = window.matchMedia('(max-width: 900px)')
    return !query.matches
  })

  useEffect(() => {
    if (!hasWindow) return undefined

    const query = window.matchMedia('(max-width: 900px)')
    const handleChange = (event: MediaQueryListEvent) => {
      setIsMobile(event.matches)
      setIsOpen(event.matches ? false : true)
    }

    setIsMobile(query.matches)
    setIsOpen(query.matches ? false : true)

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handleChange)
      return () => query.removeEventListener('change', handleChange)
    }

    query.addListener(handleChange)
    return () => query.removeListener(handleChange)
  }, [])

  useEffect(() => {
    if (isMobile) {
      setIsOpen(false)
    }
  }, [pathname, isMobile])

  useEffect(() => {
    if (!hasWindow) return
    if (!isMobile) return
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isMobile, isOpen])

  useEffect(() => {
    if (!hasWindow) return
    if (!isMobile) return

    const previousOverflow = document.body.style.overflow
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = previousOverflow
    }

    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isMobile, isOpen])

  useEffect(() => {
    if (!hasWindow) return
    const root = document.body
    if (!isMobile && isOpen) {
      root.style.setProperty('--sidebar-width', '240px')
      root.classList.add('desktop-sidebar-open')
      root.classList.remove('desktop-sidebar-collapsed')
    } else if (!isMobile) {
      root.style.setProperty('--sidebar-width', '0px')
      root.classList.add('desktop-sidebar-collapsed')
      root.classList.remove('desktop-sidebar-open')
    } else {
      root.style.removeProperty('--sidebar-width')
      root.classList.remove('desktop-sidebar-open')
      root.classList.remove('desktop-sidebar-collapsed')
    }

    return () => {
      root.style.removeProperty('--sidebar-width')
      root.classList.remove('desktop-sidebar-open')
      root.classList.remove('desktop-sidebar-collapsed')
    }
  }, [isMobile, isOpen])

  const navClassName = useMemo(() => {
    const classes = ['sidebar', isMobile ? 'sidebar-mobile' : 'sidebar-desktop']
    if (isMobile) {
      classes.push(isOpen ? 'sidebar-open' : 'sidebar-closed')
    } else if (!isOpen) {
      classes.push('sidebar-hidden')
    }
    return classes.join(' ')
  }, [isMobile, isOpen])

  const makeLinkClass = (targetPath: string) => {
    const normalized = targetPath.toLowerCase()
    const isActive = pathname === normalized || pathname.startsWith(`${normalized}/`)
    return isActive ? 'sidebar-link active' : 'sidebar-link'
  }

  return (
    <>
      {isMobile && isOpen && (
        <div
          className="sidebar-backdrop"
          aria-hidden="true"
          onClick={() => setIsOpen(false)}
        />
      )}
      <nav id="app-sidebar" className={navClassName} aria-label={t('sidebar.ariaMainNav')}>
        <div className="sidebar-content">
          <div className="sidebar-heading">{t('sidebar.heading')}</div>
          <ul className="sidebar-menu">
            {PRIMARY_NAV_ITEMS.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={makeLinkClass(item.path)}
                  onClick={() => {
                    if (isMobile) setIsOpen(false)
                  }}
                >
                  {t(item.labelKey)}
                </Link>
              </li>
            ))}
            {showCrewModeNav && (
              <li key="/crew">
                <Link
                  to="/crew"
                  className={makeLinkClass('/crew')}
                  onClick={() => {
                    if (isMobile) setIsOpen(false)
                  }}
                >
                  {t('nav.crewMode')}
                </Link>
              </li>
            )}
          </ul>
        </div>
      </nav>

      <button
        type="button"
        className={`sidebar-toggle ${isOpen ? 'sidebar-toggle-open' : ''} ${isMobile ? 'sidebar-toggle-mobile' : 'sidebar-toggle-desktop'}`}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={isOpen ? t('sidebar.closeNav') : t('sidebar.openNav')}
        aria-controls="app-sidebar"
        aria-expanded={isOpen}
      >
        {isOpen ? t('sidebar.close') : t('sidebar.menu')}
      </button>
    </>
  )
}
